export type PerCornerRadius = {
  topLeft: number;
  topRight: number;
  bottomRight: number;
  bottomLeft: number;
};

export type BorderRadiusValue = number | PerCornerRadius;

export const isPerCorner = (value: unknown): value is PerCornerRadius =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as PerCornerRadius).topLeft === "number" &&
  typeof (value as PerCornerRadius).topRight === "number" &&
  typeof (value as PerCornerRadius).bottomRight === "number" &&
  typeof (value as PerCornerRadius).bottomLeft === "number";

export const toUniform = (value: BorderRadiusValue): number => {
  if (typeof value === "number") return value;
  return Math.max(
    value.topLeft,
    value.topRight,
    value.bottomRight,
    value.bottomLeft
  );
};

export const toPerCorner = (value: BorderRadiusValue): PerCornerRadius => {
  if (isPerCorner(value)) return value;
  const n = typeof value === "number" ? value : 0;
  return { topLeft: n, topRight: n, bottomRight: n, bottomLeft: n };
};

export const toCssValue = (value: BorderRadiusValue): string => {
  if (isPerCorner(value)) {
    return `${value.topLeft}px ${value.topRight}px ${value.bottomRight}px ${value.bottomLeft}px`;
  }
  return `${typeof value === "number" ? value : 0}px`;
};

export const scaleBorderRadius = (
  value: BorderRadiusValue,
  scale: number
): BorderRadiusValue => {
  if (isPerCorner(value)) {
    return {
      topLeft: value.topLeft * scale,
      topRight: value.topRight * scale,
      bottomRight: value.bottomRight * scale,
      bottomLeft: value.bottomLeft * scale
    };
  }
  return (typeof value === "number" ? value : 0) * scale;
};

export const buildRoundedRectPath = (
  x: number,
  y: number,
  w: number,
  h: number,
  tl: number,
  tr: number,
  br: number,
  bl: number
): string => {
  // Proportional clamp matching CSS border-radius / <rect rx ry>: if any
  // adjacent pair of corner radii exceeds the side they share, scale ALL
  // four radii by the most restrictive ratio. Without this, the L segments
  // between curves run backwards and downstream filters (feMorphology) emit
  // rectangular artefacts on the overflowing sides.
  const ratios = [
    tl + tr > w ? w / (tl + tr) : 1,
    tr + br > h ? h / (tr + br) : 1,
    br + bl > w ? w / (br + bl) : 1,
    bl + tl > h ? h / (bl + tl) : 1
  ];
  const scale = Math.min(1, ...ratios);
  const ctl = tl * scale;
  const ctr = tr * scale;
  const cbr = br * scale;
  const cbl = bl * scale;
  // Use A (elliptical arc) rather than Q (quadratic bezier) so corners are
  // true quarter-circles, matching CSS border-radius and <rect rx ry>. A
  // quadratic bezier with the corner as its control point is a noticeably
  // flatter curve than a true arc — the divergence becomes obvious once
  // radii approach side/2 (e.g. linked rounding at max), where the body
  // renders as a circle but a Q-based outline would render as a squircle.
  return [
    `M ${x + ctl} ${y}`,
    `L ${x + w - ctr} ${y}`,
    `A ${ctr} ${ctr} 0 0 1 ${x + w} ${y + ctr}`,
    `L ${x + w} ${y + h - cbr}`,
    `A ${cbr} ${cbr} 0 0 1 ${x + w - cbr} ${y + h}`,
    `L ${x + cbl} ${y + h}`,
    `A ${cbl} ${cbl} 0 0 1 ${x} ${y + h - cbl}`,
    `L ${x} ${y + ctl}`,
    `A ${ctl} ${ctl} 0 0 1 ${x + ctl} ${y}`,
    "Z"
  ].join(" ");
};
